'use client';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { pageCustomerDirectories } from '@/constant/route';
import { useQueryShowUiProcedureTypeTree } from '@/hooks/query/use-query-procedure-type-tree';
import { useQueryThongTinGiangVien } from '@/hooks/query/use-query-thong-tin-giang-vien';
import { useURLState } from '@/hooks/use-url-state';
import { cn } from '@/lib/utils';
import { usePathname, useRouter } from '@/navigation';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import {
    collapseSingleChildBranches,
    filterEligibleProcedureTypeTree,
    resolvePTypeAutoRedirect,
    resolveUserRole,
    treeHasStaffCategoryNode,
    withStaffCategory,
} from '@/server/api/v1/procedure-type/procedure-type-eligibility';
import { ProcedureTypeResponse } from '@/server/api/v1/procedure-type/type';
import { ChevronDown, ChevronLeft, ChevronRight, StickyNote } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import {
    PROCEDURE_FIELDS_PARAM_KEY,
    PROCEDURE_TYPE_ALL_VALUE,
    PROCEDURE_TYPE_PARAM_KEY,
} from '../../type';

/**
 * Đọc mode cá nhân hoá từ env. Cần `NEXT_PUBLIC_` để bundle ra
 * client. Mặc định 'public' (toàn bộ cây loại thủ tục hiển thị, không
 * lọc theo user).
 */
const PROCEDURE_TYPE_MODE: 'public' | 'personalized' =
    process.env.NEXT_PUBLIC_PROCEDURE_TYPE_MODE === 'personalized'
        ? 'personalized'
        : 'public';

/**
 * Callback chọn p-type, truyền xuống từng item. null = "Tất cả".
 *
 * QUAN TRỌNG — vì sao KHÔNG dùng `<Link>` nữa:
 *  - Trước đây mỗi item là `<Link href="/procedure-list?p-types=X">`. Click =
 *    full Next navigation → RSC round-trip qua layout async (`auth()`...) →
 *    cảm giác remount + giật mạnh.
 *  - Thử chặn bằng `e.preventDefault()` thì điều hướng vẫn race với cập nhật
 *    shallow: href KHÔNG kế thừa param khác (theo thiết kế cũ) nên khi nó
 *    thắng -> URL bị strip sạch `p-fields`/sort/page và mất luôn `p-types`
 *    (về `/procedure-list` trống, không load data).
 *  - Giải pháp: bỏ hẳn `<Link>`, chỉ cập nhật `p-types` qua nuqs (shallow):
 *    đổi URL phía client, GIỮ các param khác (p-fields...), KHÔNG round-trip
 *    server. `procedure-cust` đọc cùng key nên tự đồng bộ + tự reset trang.
 */
type OnSelectPType = (pTypeValue: string | null) => void;

/**
 * Tính giá trị p-types cho item cha:
 * - Tập hợp tất cả ValueStr của các con khác rỗng, nối bằng dấu phẩy
 * - Nếu không có con hợp lệ → dùng ValueStr của chính cha
 */
function getParentPTypeValue(item: ProcedureTypeResponse): string {
    if (!item.Childrens?.length) return item.ValueStr ?? '';

    const validValues = item.Childrens.filter(
        (c) => !!c.ValueStr && c.ValueStr.length > 0
    ).map((c) => c.ValueStr);

    return validValues.length > 0
        ? validValues.join(',')
        : (item.ValueStr ?? '');
}

/**
 * Hàm thuần: item cha có đang active không.
 * Active khi p-types hiện tại chứa ít nhất một ValueStr của các con.
 * Nhận currentPType qua tham số để không subscribe useSearchParams ở mỗi item.
 */
function isParentActive(
    menu: ProcedureTypeResponse,
    currentPType: string
): boolean {
    const parentValues =
        menu.Childrens?.filter(
            (c) => !!c.ValueStr && c.ValueStr.length > 0
        ).map((c) => c.ValueStr) ?? [];

    if (parentValues.length === 0) {
        return currentPType === (menu.ValueStr ?? '');
    }

    const currentValues = currentPType.split(',').filter(Boolean);
    return parentValues.some((v) => currentValues.includes(v));
}

/**
 * Hàm thuần: item lá có đang active không.
 * Chỉ active khi p-types có đúng 1 giá trị và bằng ValueStr của item.
 */
function isLeafActive(
    child: ProcedureTypeResponse,
    currentPType: string
): boolean {
    const values = currentPType.split(',').filter(Boolean);
    return values.length === 1 && values[0] === (child.ValueStr ?? '');
}

// ─── Desktop sub-components ────────────────────────────────────────────────

/** Desktop: item lá (con) trong dropdown hover */
const DesktopLeafItem = ({
    child,
    currentPType,
    onSelect,
}: {
    child: ProcedureTypeResponse;
    currentPType: string;
    onSelect: OnSelectPType;
}) => {
    const trans = useTranslations('ProcedureTypeManagement');
    const isActive = isLeafActive(child, currentPType);
    const pTypeValue = child.ValueStr ?? '';

    return (
        <DropdownMenuItem
            onSelect={() => onSelect(pTypeValue)}
            className={cn(
                'cursor-pointer gap-x-2 px-4 py-2.5 transition-colors duration-150 hover:bg-amber-50 hover:text-color-1 focus:bg-amber-50 focus:text-color-1',
                isActive ? 'bg-amber-50 text-color-1' : 'text-gray-700'
            )}
        >
            <span className='line-clamp-1'>
                {child.AppLanguageResourceCode &&
                child.AppLanguageResourceCode.length
                    ? trans(child.AppLanguageResourceCode as any)
                    : child.Name}
            </span>
        </DropdownMenuItem>
    );
};

/**
 * Desktop: item cha kèm dropdown con khi hover.
 *
 * Dùng Radix `DropdownMenu` (controlled, mở khi hover) thay cho dropdown
 * absolute như trước — vì thanh nav giờ cuộn ngang (`overflow-x-auto`), nội
 * dung absolute đặt `top-full` sẽ bị CẮT theo trục dọc. `DropdownMenuContent`
 * render qua Portal nên không bị container cuộn cắt. `modal={false}` để hover
 * sang item khác / bấm nút cuộn vẫn hoạt động (không khoá pointer toàn trang).
 *
 * `shrink-0` + `whitespace-nowrap`: item GIỮ đủ bề rộng chữ, không bị bóp méo
 * khi có quá nhiều loại thủ tục — phần thừa sẽ cuộn ngang.
 */
const DesktopParentItem = ({
    menu,
    currentPType,
    onSelect,
}: {
    menu: ProcedureTypeResponse;
    currentPType: string;
    onSelect: OnSelectPType;
}) => {
    const trans = useTranslations('ProcedureTypeManagement');
    const hasChildren = !!menu.Childrens?.length;
    const isActive = isParentActive(menu, currentPType);
    const pTypeValue = getParentPTypeValue(menu);

    const [open, setOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openNow = useCallback(() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpen(true);
    }, []);
    // Đóng có trễ nhỏ để di chuột qua khoảng hở giữa item và dropdown không tắt.
    const closeSoon = useCallback(() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 120);
    }, []);
    useEffect(
        () => () => {
            if (closeTimer.current) clearTimeout(closeTimer.current);
        },
        []
    );

    const label =
        menu.AppLanguageResourceCode && menu.AppLanguageResourceCode.length
            ? trans(menu.AppLanguageResourceCode as any)
            : menu.Name;

    const triggerButton = (
        <button
            type='button'
            onClick={() => onSelect(pTypeValue)}
            className={cn(
                'flex shrink-0 items-center gap-x-1 whitespace-nowrap rounded-full px-3 py-1 transition-colors duration-200',
                isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-primary/10 hover:text-color-1'
            )}
        >
            <span>{label}</span>
            {/* Hiện mũi tên chỉ khi có con */}
            {hasChildren && (
                <ChevronDown
                    strokeWidth={2}
                    className={cn(
                        'h-4 w-4 min-w-4 transition-transform duration-200',
                        open && 'rotate-180'
                    )}
                />
            )}
        </button>
    );

    if (!hasChildren) {
        return (
            <div className='flex h-full shrink-0 items-center'>
                {triggerButton}
            </div>
        );
    }

    return (
        <div
            className='flex h-full shrink-0 items-center'
            onMouseEnter={openNow}
            onMouseLeave={closeSoon}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                    {triggerButton}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align='start'
                    sideOffset={10}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    onMouseEnter={openNow}
                    onMouseLeave={closeSoon}
                    className='w-56'
                >
                    {menu.Childrens?.map((child, childIndex) => (
                        <DesktopLeafItem
                            key={child.ID ?? childIndex}
                            child={child}
                            currentPType={currentPType}
                            onSelect={onSelect}
                        />
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

/**
 * Desktop: vùng cuộn ngang chứa các item loại thủ tục.
 *
 * Khi tổng bề rộng item vượt quá chỗ trống, dải sẽ cuộn ngang (ẩn scrollbar)
 * và hiện nút mũi tên ‹ › ở hai mép (kèm gradient mờ) để báo còn nội dung và
 * cho phép cuộn bằng chuột. Trạng thái nút tự cập nhật theo vị trí cuộn / khi
 * đổi danh sách / khi resize.
 */
const DesktopScrollStrip = ({ children }: { children: ReactNode }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanLeft(scrollLeft > 1);
        setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateArrows();
        // Theo dõi thay đổi kích thước container + từng item (font load, đổi list).
        const ro = new ResizeObserver(updateArrows);
        ro.observe(el);
        Array.from(el.children).forEach((c) => ro.observe(c));
        return () => ro.disconnect();
    }, [updateArrows, children]);

    const scrollByDir = useCallback((dir: 1 | -1) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({
            left: dir * Math.max(el.clientWidth * 0.7, 160),
            behavior: 'smooth',
        });
    }, []);

    return (
        <div className='relative flex h-full min-w-0 flex-1 items-center'>
            {/* Nút cuộn trái — chỉ hiện khi còn nội dung bên trái */}
            {canLeft && (
                <button
                    type='button'
                    aria-label='Cuộn sang trái'
                    onClick={() => scrollByDir(-1)}
                    className='absolute left-0 z-20 flex h-full items-center bg-gradient-to-r from-background via-background to-transparent pl-1 pr-8 text-primary transition-colors hover:text-color-1'
                >
                    <ChevronLeft className='h-5 w-5' />
                </button>
            )}

            {/* Dải cuộn ngang (ẩn scrollbar) */}
            <div
                ref={scrollRef}
                onScroll={updateArrows}
                className='flex h-full items-center gap-x-3 overflow-x-auto overflow-y-hidden px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            >
                {children}
            </div>

            {/* Nút cuộn phải — chỉ hiện khi còn nội dung bên phải */}
            {canRight && (
                <button
                    type='button'
                    aria-label='Cuộn sang phải'
                    onClick={() => scrollByDir(1)}
                    className='absolute right-0 z-20 flex h-full items-center bg-gradient-to-l from-background via-background to-transparent pl-8 pr-1 text-primary transition-colors hover:text-color-1'
                >
                    <ChevronRight className='h-5 w-5' />
                </button>
            )}
        </div>
    );
};

// ─── Mobile sub-components ────────────────────────────────────────────────

/** Mobile: item lá (con) trong submenu dropdown */
const MobileLeafItem = ({
    child,
    currentPType,
    onSelect,
}: {
    child: ProcedureTypeResponse;
    currentPType: string;
    onSelect: OnSelectPType;
}) => {
    const isActive = isLeafActive(child, currentPType);
    const pTypeValue = child.ValueStr ?? '';

    return (
        <DropdownMenuItem
            onSelect={() => onSelect(pTypeValue)}
            className={cn(
                'flex items-center gap-x-2',
                isActive && 'bg-amber-50 text-color-1'
            )}
        >
            <span className='line-clamp-1'>{child.Name}</span>
        </DropdownMenuItem>
    );
};

/**
 * Mobile: item cha trong dropdown.
 * - Phần tên → chọn p-type tổng hợp của tất cả con (đóng menu)
 * - Phần mũi tên (DropdownMenuSubTrigger rỗng) → mở submenu con
 */
const MobileParentItem = ({
    menu,
    currentPType,
    onSelect,
}: {
    menu: ProcedureTypeResponse;
    currentPType: string;
    onSelect: OnSelectPType;
}) => {
    const hasChildren = !!menu.Childrens?.length;
    const isActive = isParentActive(menu, currentPType);
    const pTypeValue = getParentPTypeValue(menu);

    if (hasChildren) {
        return (
            <DropdownMenuSub>
                <div
                    className={cn(
                        'flex items-center',
                        isActive && 'font-semibold text-color-1'
                    )}
                >
                    {/* Tên item cha — chọn p-type (shallow) */}
                    <button
                        type='button'
                        onClick={() => onSelect(pTypeValue)}
                        className={cn(
                            'flex flex-1 items-center gap-x-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
                            isActive && 'text-color-1'
                        )}
                    >
                        <span className='line-clamp-1'>{menu.Name}</span>
                    </button>
                    {/* Chỉ phần mũi tên mở submenu — tách riêng để click vào tên không trigger submenu */}
                    <DropdownMenuSubTrigger className='px-2 py-1.5' />
                </div>
                <DropdownMenuSubContent className='w-48'>
                    {menu.Childrens?.map((child, childIndex) => (
                        <MobileLeafItem
                            key={child.ID ?? childIndex}
                            child={child}
                            currentPType={currentPType}
                            onSelect={onSelect}
                        />
                    ))}
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        );
    }

    // Item lá (không có con) — chọn thẳng
    return (
        <DropdownMenuItem
            onSelect={() => onSelect(pTypeValue)}
            className={cn(
                'flex items-center gap-x-2',
                isActive && 'bg-amber-50 font-semibold text-color-1'
            )}
        >
            <span className='line-clamp-1'>{menu.Name}</span>
        </DropdownMenuItem>
    );
};

// ─── Main Component ────────────────────────────────────────────────────────

const SubHeaderNavbar = ({ className }: { className?: string }) => {
    const pathname = usePathname();
    const router = useRouter();
    const transSidebar = useTranslations('Sidebar');

    // p-types đọc/ghi qua nuqs (shallow). Cùng key với `procedure-cust` nên
    // hai bên tự đồng bộ mà KHÔNG cần điều hướng Next (không RSC round-trip).
    const [currentPType, setPType] = useURLState(PROCEDURE_TYPE_PARAM_KEY);
    // Lọc lĩnh vực (p-fields) — clear khi đổi p-type.
    const [, setFieldCodes] = useURLState(PROCEDURE_FIELDS_PARAM_KEY);

    /**
     * Đánh dấu user đã CHỦ ĐỘNG chọn p-type (kể cả chọn "Tất cả" = xoá
     * p-types). Khi đã chủ động chọn thì auto-redirect default KHÔNG được
     * ghi đè nữa — nếu không, bấm "Tất cả" ở public mode sẽ bị effect set
     * lại defaultPTypes ngay lập tức (nút trông như không hoạt động).
     */
    const userInteractedRef = useRef(false);

    /**
     * Chọn p-type: cập nhật URL shallow (client-only). null/'' = "Tất cả"
     * (xoá p-types). Đồng thời XOÁ p-fields (lĩnh vực) nếu đang có — đổi nhóm
     * đối tượng thì lĩnh vực cũ không còn phù hợp.
     * - history 'push': bấm Back quay lại trạng thái trước.
     * - shallow true: KHÔNG báo cho Next router → không re-render server.
     * - Hai setter gọi cùng tick → nuqs gộp thành 1 lần cập nhật URL.
     */
    const onSelectPType = useCallback<OnSelectPType>(
        (pTypeValue) => {
            userInteractedRef.current = true;

            // Đang ở trang khác (trang chủ, chi tiết thủ tục...) -> phải điều
            // hướng VỀ /procedure-list kèm p-types đã chọn. Dùng full navigation
            // vì cần đổi pathname; shallow update chỉ áp dụng khi đã ở đúng trang.
            // null/'' = "Tất cả" -> không gắn p-types. Không mang theo p-fields.
            if (pathname !== pageCustomerDirectories.ProcedureList) {
                const qs =
                    pTypeValue && pTypeValue.length > 0
                        ? `?${PROCEDURE_TYPE_PARAM_KEY}=${encodeURIComponent(pTypeValue)}`
                        : '';
                router.push(`${pageCustomerDirectories.ProcedureList}${qs}`);
                return;
            }

            const opts = {
                history: 'push',
                shallow: true,
                scroll: false,
            } as const;
            setPType(
                pTypeValue && pTypeValue.length > 0 ? pTypeValue : null,
                opts
            );
            setFieldCodes(null, opts);
        },
        [pathname, router, setPType, setFieldCodes]
    );

    // Lấy cây loại thủ tục đầy đủ từ API (đã filter IsShowUi=true).
    const { data: rawProcedureTypes, isLoading } =
        useQueryShowUiProcedureTypeTree();

    // ─── Cá nhân hoá ────────────────────────────────────────────────
    // Khi mode = 'personalized':
    //  1. Lọc cây theo eligibility của user (Student/Staff/Visitor +
    //     RegexBuilderJson).
    //  2. Gộp các nhánh single-child thành 1 label (vd: "Sinh viên"
    //     → 1 con "Chính quy" → 1 cháu "Loại 1" ⇒ hiển thị "Sinh viên
    //     Chính quy Loại 1" làm 1 item phẳng).
    // Khi mode = 'public': giữ nguyên cây.
    const authData = useAuthenticationStore((x) => x.auth);
    const userRole = useMemo(() => resolveUserRole(authData), [authData]);

    // ─── Phân loại cán bộ (HRM) ─────────────────────────────────────
    // `SoHieuCongChuc` KHÔNG suy ra được nhóm cán bộ (VC/NLĐ, quản lý, BGH) —
    // phải lấy `MaPhanLoai` từ HRM. Chỉ gọi khi: mode personalized + user là cán
    // bộ + cây có node gate theo phân loại (`employee-*`). Trước khi HRM settle,
    // coi như đang tải để không render nav / auto-redirect nhầm.
    const needStaffCategory =
        PROCEDURE_TYPE_MODE === 'personalized' &&
        userRole?.role === 'Staff' &&
        treeHasStaffCategoryNode(rawProcedureTypes);
    const { data: thongTinGiangVien, isReady: isStaffCategoryReady } =
        useQueryThongTinGiangVien(
            // Cùng mã dùng cho `HRM_GetThongTinGiangVien` ở submit-dossier
            // (`StaffInfo.SoHieuCongChuc`) = `identityCode` của role Staff.
            userRole?.identityCode ?? '',
            needStaffCategory
        );
    const effectiveUserRole = useMemo(
        () => withStaffCategory(userRole, thongTinGiangVien),
        [userRole, thongTinGiangVien]
    );

    // Cây/redirect chưa quyết định được khi còn chờ phân loại cán bộ.
    const isTreeResolving =
        isLoading || (needStaffCategory && !isStaffCategoryReady);

    const { procedureTypes, isPersonalizedNoMatch } = useMemo<{
        procedureTypes: ProcedureTypeResponse[];
        isPersonalizedNoMatch: boolean;
    }>(() => {
        if (PROCEDURE_TYPE_MODE !== 'personalized') {
            return {
                procedureTypes: rawProcedureTypes,
                isPersonalizedNoMatch: false,
            };
        }
        const filtered = filterEligibleProcedureTypeTree(
            rawProcedureTypes,
            effectiveUserRole
        );
        // User đã đăng nhập (có role) nhưng KHÔNG khớp eligibility của loại
        // thủ tục nào → fallback "Tất cả": nav chỉ hiện mục Tất cả, list chỉ
        // lấy thủ tục dùng chung (Types_Token rỗng). Yêu cầu rawProcedureTypes
        // đã có data để không nhầm với lúc đang tải.
        if (
            effectiveUserRole &&
            rawProcedureTypes.length > 0 &&
            filtered.length === 0
        ) {
            return { procedureTypes: [], isPersonalizedNoMatch: true };
        }
        return {
            procedureTypes: collapseSingleChildBranches(filtered),
            isPersonalizedNoMatch: false,
        };
    }, [rawProcedureTypes, effectiveUserRole]);

    // Xác định tên label hiển thị trên mobile trigger
    const activeType = procedureTypes.find((t) => {
        const validValues =
            t.Childrens?.filter(
                (c) => !!c.ValueStr && c.ValueStr.length > 0
            ).map((c) => c.ValueStr) ?? [];

        if (validValues.length === 0)
            return (t.ValueStr ?? '') === currentPType;

        const currentValues = currentPType.split(',').filter(Boolean);
        return validValues.some((v) => currentValues.includes(v));
    });

    // ─── Auto-redirect p-types ───────────────────────────────────────
    // Áp dụng cho cả 2 mode khi user đang ở `/procedure-list`:
    //  - Mode 'public': nếu URL không có p-types → set p-types mặc
    //    định theo user (giúp khách hàng vào trang đã thấy đúng nhóm
    //    thủ tục của họ).
    //  - Mode 'personalized': như trên + nếu p-types hiện tại KHÔNG
    //    nằm trong cây user được phép → ép về default.
    //
    // Quyết định redirect dùng chung `resolvePTypeAutoRedirect` với hook gate
    // bảng thủ tục (`use-procedure-selection-ready`) để điều kiện CHỜ của bảng
    // luôn KHỚP với điều kiện redirect ở đây — bảng chỉ fetch sau khi p-types
    // đã ổn định.
    useEffect(() => {
        if (pathname !== pageCustomerDirectories.ProcedureList) return;

        const { shouldRedirect, target } = resolvePTypeAutoRedirect({
            rawProcedureTypes,
            isTreeLoading: isTreeResolving,
            userRole: effectiveUserRole,
            currentPType,
            mode: PROCEDURE_TYPE_MODE,
            userInteracted: userInteractedRef.current,
        });

        if (!shouldRedirect || target === null) return;

        // Auto-redirect dùng 'replace' (không tạo history thừa) + shallow.
        setPType(target, {
            history: 'replace',
            shallow: true,
            scroll: false,
        });
    }, [
        isTreeResolving,
        pathname,
        currentPType,
        rawProcedureTypes,
        effectiveUserRole,
        setPType,
    ]);

    // Giá trị p-types mà mục "Tất cả" sẽ chọn:
    //  - personalized không-khớp → sentinel 'all' (chỉ thủ tục Types_Token rỗng)
    //  - còn lại (public) → null (xoá p-types = lấy mọi thủ tục)
    const allItemValue = isPersonalizedNoMatch ? PROCEDURE_TYPE_ALL_VALUE : null;

    // Mục "Tất cả" hiển thị khi: public mode (như cũ) HOẶC personalized nhưng
    // user không khớp loại nào (chỉ còn mục Tất cả).
    const showAllItem =
        PROCEDURE_TYPE_MODE !== 'personalized' || isPersonalizedNoMatch;

    // Active của mục "Tất cả": đang ở trang procedure-list và p-types khớp
    // giá trị tương ứng (sentinel ở personalized không-khớp, rỗng ở public).
    const isAllActive =
        pathname === pageCustomerDirectories.ProcedureList &&
        (isPersonalizedNoMatch
            ? currentPType === PROCEDURE_TYPE_ALL_VALUE
            : !currentPType);

    // Nhãn hiển thị (mobile trigger): "Tất cả" khi chưa chọn loại nào hoặc
    // đang ở sentinel; ngược lại hiển thị tên loại đang chọn.
    const activeLabel =
        !currentPType || currentPType === PROCEDURE_TYPE_ALL_VALUE
            ? transSidebar('Sidebar_Procedure_All')
            : (activeType?.Name ?? currentPType);

    return (
        <>
            {/* ===== DESKTOP NAV: từ md trở lên ===== */}
            <nav
                className={cn(
                    'hidden h-full w-full items-center pl-6 pr-3 text-sm font-semibold text-primary md:flex',
                    className
                )}
            >
                {/* Phần GHIM (không cuộn): nhãn tiêu đề + "Tất cả" + vạch ngăn.
                    Luôn hiển thị để nút reset "Tất cả" luôn trong tầm với. */}
                <div className='flex h-full shrink-0 items-center gap-x-3 pr-1'>
                    {/* Nhãn tiêu đề — chỉ hiện trên xl */}
                    <div className='hidden h-full items-center justify-center text-balance text-sm font-bold xl:flex'>
                        {transSidebar('Sidebar_ProcedureList')}:
                    </div>

                    {/* Item tĩnh: Tất cả thủ tục — public: xoá p-types;
                        personalized không-khớp: sentinel 'all'. */}
                    {showAllItem && (
                        <>
                            <button
                                type='button'
                                onClick={() => onSelectPType(allItemValue)}
                                className={cn(
                                    'flex shrink-0 items-center gap-x-1 whitespace-nowrap rounded-full px-3 py-1 transition-colors duration-200',
                                    isAllActive
                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                        : 'hover:bg-primary/10 hover:text-color-1'
                                )}
                            >
                                <StickyNote
                                    strokeWidth={2}
                                    className='h-4 w-4 min-w-4'
                                />
                                <span>
                                    {transSidebar('Sidebar_Procedure_All')}
                                </span>
                            </button>

                            <div
                                className={cn(
                                    'flex h-full w-1 select-none items-center text-gray-300',
                                    {
                                        hidden:
                                            isTreeResolving ||
                                            procedureTypes.length == 0,
                                    }
                                )}
                            >
                                |
                            </div>
                        </>
                    )}
                </div>

                {/* Skeleton khi đang tải dữ liệu từ API (kèm chờ phân loại cán bộ) */}
                {isTreeResolving ? (
                    <div className='flex h-full flex-1 items-center gap-x-3 px-1'>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className='h-7 w-24 rounded-full' />
                        ))}
                    </div>
                ) : (
                    // Vùng cuộn ngang cho các loại thủ tục động (đọc đủ chữ).
                    <DesktopScrollStrip>
                        {procedureTypes.map((menu, index) => (
                            <DesktopParentItem
                                key={menu.ID ?? index}
                                menu={menu}
                                currentPType={currentPType}
                                onSelect={onSelectPType}
                            />
                        ))}
                    </DesktopScrollStrip>
                )}
            </nav>

            {/* ===== MOBILE NAV: dưới md ===== */}
            <div
                className={cn(
                    'flex h-full w-full items-center px-3 md:hidden',
                    className
                )}
            >
                <div className='hidden pl-3 text-sm font-bold text-primary sm:block'>
                    {transSidebar('Sidebar_ProcedureList')}
                </div>

                {/* Skeleton khi đang tải */}
                {isTreeResolving ? (
                    <div className='mx-2 flex-1'>
                        <Skeleton className='h-7 w-32 rounded-full' />
                    </div>
                ) : (
                    <DropdownMenu>
                        <DropdownMenuTrigger className='flex-1 items-center justify-between gap-x-1 text-sm font-semibold text-primary outline-none'>
                            <div className='mx-2 flex h-full items-center justify-center gap-x-2 rounded-full bg-primary p-1 text-primary-foreground'>
                                <div>{activeLabel}</div>
                                <ChevronDown className='h-5 w-5' />
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='start' className='w-full'>
                            {/* Item tĩnh: Tất cả — public: xoá p-types;
                                personalized không-khớp: sentinel 'all'. */}
                            <DropdownMenuItem
                                onSelect={() => onSelectPType(allItemValue)}
                                className={cn(
                                    'flex items-center gap-x-2',
                                    isAllActive &&
                                        'bg-amber-50 font-semibold text-color-1'
                                )}
                            >
                                <StickyNote
                                    strokeWidth={2}
                                    className='h-4 w-4 min-w-4'
                                />
                                <span className='line-clamp-1'>
                                    {transSidebar('Sidebar_Procedure_All')}
                                </span>
                            </DropdownMenuItem>

                            {/* Các item động từ API */}
                            {procedureTypes.map((menu, index) => (
                                <MobileParentItem
                                    key={menu.ID ?? index}
                                    menu={menu}
                                    currentPType={currentPType}
                                    onSelect={onSelectPType}
                                />
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        </>
    );
};

export default SubHeaderNavbar;
